import { useState } from 'react';
import { Badge, Button, Group, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import {
  IconRefresh,
  IconSearch,
  IconTrash,
  IconArrowsExchange,
  IconCircleX,
} from '@tabler/icons-react';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { api } from 'src/common/utils/api';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { AdminCalendar, useAdminCalendars } from './useAdminCalendars';
import { CalendarSweepPanel } from './CalendarSweepPanel';

type RecallStatus = {
  recallExists: boolean;
  recallStatus: string | null;
  recallEmail: string | null;
};

export default function CalendarHealthTab() {
  return <CompanyWrapper contentComponent={CalendarHealthContent} />;
}

function CalendarHealthContent({ company }: { company: Company }) {
  const { calendars, isLoading, isError, refetch, isRefetching } = useAdminCalendars(company.id);
  const [recallStatuses, setRecallStatuses] = useState<Record<string, RecallStatus>>({});
  const [actionsInProgress, setActionsInProgress] = useState<Set<string>>(new Set());
  const [hardDeleteTarget, setHardDeleteTarget] = useState<AdminCalendar | null>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [hardDeleteError, setHardDeleteError] = useState<{ message: string } | null>(null);

  if (isLoading) return <PageLoader />;
  if (isError || !calendars) return <Text c="red">Failed to load calendars</Text>;

  const actionKey = (id: string, action: string) => `${id}:${action}`;
  const isActionLoading = (id: string, action: string) =>
    actionsInProgress.has(actionKey(id, action));

  const withAction = async (id: string, action: string, fn: () => Promise<void>) => {
    const key = actionKey(id, action);
    setActionsInProgress(prev => new Set(prev).add(key));
    try {
      await fn();
    } catch (error) {
      notifyAndLogError('Calendar action failed', `${action} failed for ${id}`, error);
    } finally {
      setActionsInProgress(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const checkRecallStatus = async (externalId: string) => {
    await withAction(externalId, 'check', async () => {
      const { data } = await api.get<RecallStatus>(`admin/calendars/${externalId}/recall-status`);
      setRecallStatuses(prev => ({ ...prev, [externalId]: data }));
    });
  };

  const deleteFromRecall = async (externalId: string) => {
    await withAction(externalId, 'deleteRecall', async () => {
      await api.delete(`admin/calendars/${externalId}/recall`);
      await checkRecallStatus(externalId);
    });
  };

  const softDeleteLocally = async (calendarId: string) => {
    await withAction(calendarId, 'deleteLocal', async () => {
      await api.delete(`admin/companies/${company.id}/calendars/${calendarId}`);
      await refetch();
    });
  };

  const requestHardDelete = (calendar: AdminCalendar) => {
    setHardDeleteTarget(calendar);
    setHardDeleteError(null);
    openConfirm();
  };

  const confirmHardDelete = async () => {
    if (!hardDeleteTarget) return;
    setHardDeleteLoading(true);
    setHardDeleteError(null);
    try {
      await api.delete(`admin/companies/${company.id}/calendars/${hardDeleteTarget.id}/hard`);
      closeConfirm();
      setHardDeleteTarget(null);
      await refetch();
    } catch (error) {
      setHardDeleteError({
        message: error instanceof Error ? error.message : 'Failed to hard delete calendar',
      });
    } finally {
      setHardDeleteLoading(false);
    }
  };

  const resync = async (calendarId: string) => {
    await withAction(calendarId, 'resync', async () => {
      await api.post(`admin/companies/${company.id}/calendars/${calendarId}/resync`);
      await refetch();
    });
  };

  const duplicateEmails = findDuplicateEmails(calendars);

  return (
    <Stack gap="lg">
      <CalendarSweepPanel company={company} />

      <Group justify="space-between">
        <Title order={3}>Calendar health</Title>
        <Button
          loading={isRefetching}
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </Group>

      {calendars.length === 0 ? (
        <Text c="dimmed">No calendars found for this company.</Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Platform</Table.Th>
              <Table.Th>Local status</Table.Th>
              <Table.Th>Deleted</Table.Th>
              <Table.Th>External ID</Table.Th>
              <Table.Th>Recall status</Table.Th>
              <Table.Th>Issues</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {calendars.map(calendar => (
              <CalendarRow
                key={calendar.id}
                calendar={calendar}
                recallStatus={calendar.externalId ? recallStatuses[calendar.externalId] : undefined}
                isActionLoading={(action: string) =>
                  isActionLoading(calendar.externalId ?? calendar.id, action)
                }
                isDuplicateEmail={
                  !!calendar.emailAddress && duplicateEmails.has(calendar.emailAddress)
                }
                onCheckRecall={() => calendar.externalId && checkRecallStatus(calendar.externalId)}
                onDeleteFromRecall={() =>
                  calendar.externalId && deleteFromRecall(calendar.externalId)
                }
                onSoftDeleteLocally={() => softDeleteLocally(calendar.id)}
                onHardDelete={() => requestHardDelete(calendar)}
                onResync={() => resync(calendar.id)}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <ConfirmDeleteModal
        opened={confirmOpened}
        onClose={closeConfirm}
        onConfirm={confirmHardDelete}
        title="Hard delete calendar"
        message={`Permanently delete calendar ${hardDeleteTarget?.emailAddress ?? hardDeleteTarget?.externalId ?? hardDeleteTarget?.id}? This cannot be undone.`}
        confirmLabel="Hard delete"
        error={hardDeleteError}
        isLoading={hardDeleteLoading}
      />
    </Stack>
  );
}

function CalendarRow({
  calendar,
  recallStatus,
  isActionLoading,
  isDuplicateEmail,
  onCheckRecall,
  onDeleteFromRecall,
  onSoftDeleteLocally,
  onHardDelete,
  onResync,
}: {
  calendar: AdminCalendar;
  recallStatus: RecallStatus | undefined;
  isActionLoading: (action: string) => boolean;
  isDuplicateEmail: boolean;
  onCheckRecall: () => void;
  onDeleteFromRecall: () => void;
  onSoftDeleteLocally: () => void;
  onHardDelete: () => void;
  onResync: () => void;
}) {
  const issues = getIssues(calendar, recallStatus, isDuplicateEmail);
  const actions = getActions(calendar, recallStatus);

  return (
    <Table.Tr>
      <Table.Td>{calendar.emailAddress ?? '—'}</Table.Td>
      <Table.Td>
        <Badge variant="light" size="sm">
          {calendar.platform}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={STATUS_COLORS[calendar.status]} variant="light" size="sm">
          {calendar.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        {calendar.isDeleted && (
          <Badge color="red" size="sm">
            Deleted
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace" truncate="end" maw={200}>
          {calendar.externalId ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        {recallStatus ? (
          <Badge color={recallStatus.recallExists ? 'green' : 'gray'} variant="light" size="sm">
            {recallStatus.recallExists ? recallStatus.recallStatus : 'Not in Recall'}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            Not checked
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {issues.map(issue => (
            <Tooltip key={issue.label} label={issue.tooltip}>
              <Badge color={issue.color} size="sm">
                {issue.label}
              </Badge>
            </Tooltip>
          ))}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {actions.map(action => (
            <Tooltip key={action.key} label={action.tooltip}>
              <Button
                size="compact-xs"
                variant="light"
                color={action.color}
                loading={isActionLoading(action.loadingKey)}
                leftSection={action.icon}
                onClick={
                  {
                    check: onCheckRecall,
                    deleteRecall: onDeleteFromRecall,
                    deleteLocal: onSoftDeleteLocally,
                    hardDelete: onHardDelete,
                    resync: onResync,
                  }[action.key]
                }
              >
                {action.label}
              </Button>
            </Tooltip>
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

const STATUS_COLORS: Record<string, string> = {
  CONNECTING: 'blue',
  CONNECTED: 'green',
  DISCONNECTED: 'gray',
};

type Issue = { label: string; color: string; tooltip: string };

type Action = {
  key: 'check' | 'deleteRecall' | 'deleteLocal' | 'hardDelete' | 'resync';
  loadingKey: string;
  label: string;
  tooltip: string;
  color: string;
  icon: React.ReactNode;
};

function getActions(calendar: AdminCalendar, recallStatus: RecallStatus | undefined): Action[] {
  const actions: Action[] = [];

  if (calendar.externalId) {
    actions.push({
      key: 'check',
      loadingKey: 'check',
      label: 'Check',
      tooltip: 'Check if this calendar exists in Recall',
      color: 'blue',
      icon: <IconSearch size={14} />,
    });
  }

  // Resync: useful when local status is stale compared to Recall
  if (!calendar.isDeleted && calendar.externalId && recallStatus?.recallExists) {
    actions.push({
      key: 'resync',
      loadingKey: 'resync',
      label: 'Resync',
      tooltip: 'Pull latest state from Recall and sync events',
      color: 'blue',
      icon: <IconArrowsExchange size={14} />,
    });
  }

  // Delete from Recall: calendar exists in Recall but shouldn't
  if (calendar.externalId && recallStatus?.recallExists && calendar.isDeleted) {
    actions.push({
      key: 'deleteRecall',
      loadingKey: 'deleteRecall',
      label: 'Delete from Recall',
      tooltip: 'Remove this calendar from Recall (locally already deleted)',
      color: 'red',
      icon: <IconTrash size={14} />,
    });
  }

  // Soft-delete locally: calendar is active locally but missing from Recall, or disconnected on both sides
  if (!calendar.isDeleted && recallStatus && !recallStatus.recallExists) {
    actions.push({
      key: 'deleteLocal',
      loadingKey: 'deleteLocal',
      label: 'Delete locally',
      tooltip: 'Soft-delete this calendar in the database (not in Recall)',
      color: 'orange',
      icon: <IconCircleX size={14} />,
    });
  }

  // Hard delete: already soft-deleted and confirmed gone from Recall, fully orphaned
  if (calendar.isDeleted && recallStatus && !recallStatus.recallExists) {
    actions.push({
      key: 'hardDelete',
      loadingKey: 'hardDelete',
      label: 'Hard delete',
      tooltip: 'Permanently remove this row from the database',
      color: 'red',
      icon: <IconTrash size={14} />,
    });
  }

  return actions;
}

function getIssues(
  calendar: AdminCalendar,
  recallStatus: RecallStatus | undefined,
  isDuplicateEmail: boolean,
): Issue[] {
  const issues: Issue[] = [];

  if (calendar.isDeleted && recallStatus?.recallExists) {
    issues.push({
      label: 'Mismatch',
      color: 'red',
      tooltip: 'Soft-deleted locally but still active in Recall',
    });
  }

  if (!calendar.isDeleted && calendar.externalId && recallStatus && !recallStatus.recallExists) {
    issues.push({
      label: 'Missing from Recall',
      color: 'yellow',
      tooltip: 'Active locally but not found in Recall',
    });
  }

  if (
    !calendar.isDeleted &&
    recallStatus?.recallExists &&
    recallStatus.recallStatus &&
    calendar.status.toLowerCase() !== recallStatus.recallStatus
  ) {
    issues.push({
      label: 'Status mismatch',
      color: 'yellow',
      tooltip: `Local: ${calendar.status}, Recall: ${recallStatus.recallStatus}`,
    });
  }

  if (isDuplicateEmail && !calendar.isDeleted) {
    issues.push({
      label: 'Duplicate email',
      color: 'orange',
      tooltip: 'Multiple active calendars share this email address',
    });
  }

  return issues;
}

function findDuplicateEmails(calendars: AdminCalendar[]): Set<string> {
  const activeCalendars = calendars.filter(c => !c.isDeleted && c.emailAddress);
  const emailCounts = new Map<string, number>();
  for (const cal of activeCalendars) {
    emailCounts.set(cal.emailAddress!, (emailCounts.get(cal.emailAddress!) ?? 0) + 1);
  }
  return new Set(
    [...emailCounts.entries()].filter(([, count]) => count > 1).map(([email]) => email),
  );
}
